import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import { Info } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AudienceHealthGauge } from './audience-health-gauge';

interface DataPoint {
  name: string;
  y: number;
  color?: string;
}

interface Props {
  dataSource: DataPoint[];
  height?: number;
  width?: number;
  innerSize?: string;
  size?: string;
  className?: string;
  profileImage?:string;
}

interface LegendItemProps {
  color: string;
  label: string;
  value: string;
}
const descriptionMap: Record<string, string> = {
  "Real People":
    "Accounts that demonstrate authentic engagement behaviour, including meaningful comments, contextual relevance to posts, natural language usage, and basic profile health indicators.",

  "Suspicious Accounts":
    "Accounts that show partial authenticity but exhibit mild behavioural or profile inconsistencies.",

  "Likely Fake Followers":
    "Accounts that display multiple indicators of inorganic or coordinated engagement behaviour.",

  "Bots":
    "Accounts that demonstrate automated engagement behaviour with strong signals of non-human activity.",
};


const LegendItem: React.FC<LegendItemProps> = ({
  color,
  label,
  value,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        height: 72,
        minWidth: 160,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
      }}
    >
      {/* Color bar */}
      <div
        style={{
          width: 4,
          height: "100%",
          borderRadius: 4,
          backgroundColor: color,
        }}
      />

      {/* Content */}
      {/* <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {label}
          </span>

          <div
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            style={{ cursor: "pointer" }}
          >
            <Info size={14} color="#9ca3af" />
          </div>
        </div>

        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#111827",
            marginTop: 2,
          }}
        >
          {value}
        </span>
      </div> */}

      {/* Tooltip */}
      <div
  style={{
    display: "flex",
    flexDirection:'column'
  }}
>
  <div className='d-flex items-center'>
  <span
    style={{
      fontSize: 12,
      fontWeight: 400,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing:'1px'
    }}
  >
    {label}
  </span>

  <Tooltip
    title={descriptionMap[label]}
    arrow
    placement="top"
    componentsProps={{
      tooltip: {
        sx: {
          fontWeight: 300,
          fontSize: 11,
          letterSpacing:'0.5px'
        },
      },
    }}
    
  >
    <IconButton size="small" sx={{ padding: "2px" }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
    </IconButton>
  </Tooltip>
  </div>
   <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#111827",
            marginTop: 2,
          }}
        >
          {value}
        </span>
</div>
    </div>
  );
};



export const DonutChartAdvance: React.FC<Props> = ({
  dataSource,
  height = 200,
  width = 200,
  innerSize = '80%',
  size = '80%',
  className,
  profileImage
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height,
      width,
    },
    title: { text: undefined },
    credits: { enabled: false },
    tooltip: {
      pointFormat: '<b>{point.percentage:.1f}%</b>',
    },
    legend: { enabled: false },
    plotOptions: {
      pie: {
        innerSize,
        size,
        dataLabels: { enabled: false },
        colors: dataSource.map(d => d.color ?? '#9ca3af'),
        borderWidth: 2,
        borderColor: '#ffffff',
      },
    },
    series: [
      {
        type: 'pie',
        data: dataSource,
      },
    ],
    exporting: { enabled: false },
  };

  const chartInstance = useRef<Highcharts.Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      // Create chart only once
      chartInstance.current = Highcharts.chart(chartRef.current, chartOptions);
    } else {
      // Only update data (no recreation)
      chartInstance.current.series[0].setData(dataSource);
    }
  }, [dataSource]);
  const realPeoplePercentage =
  dataSource.find(d => d.name === "Real People")?.y ?? 0;
  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
        maxWidth: '100%',
      }}
    >

<div className='audience-health-sec'
  style={{
    gap: 0,
    width: '100%',
  }}
>
  <div className='flex flex-column flex-sm-row align-items-center align-items-sm-start' style={{gap:'40px'}}>
    <AudienceHealthGauge realPeoplePercentage={realPeoplePercentage} avatarUrl={profileImage} />
    {/* Donut (fixed) */}
    <div
      style={{
        width,
        height,
        flexShrink: 0,     // ✅ prevents moving
      }}
    >
      <div ref={chartRef} className={className} />
    </div>
  </div>

  {/* Legend (flexible) */}
  <div
    className="donut-legend-grid"
  >
    {dataSource.map(item => (
      <LegendItem
        key={item.name}
        color={item.color || '#000'}
        label={item.name}
        value={`${item.y.toFixed(1)}%`}
      />
    ))}
  </div>
</div>

    </div>
  );
};